import {
  EmailAuthProvider,
  linkWithCredential,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  updatePassword,
  verifyBeforeUpdateEmail,
  type User,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { app, db } from '../firebase';
import { authAccountError } from '../errors';
import { getAuthActionCodeSettings } from './authActionUrls';
import { googleProvider } from './ssoProviders';

function requireEmail(user: User): string {
  const email = user.email?.trim();
  if (!email) throw Object.assign(new Error('No email on this account.'), { code: 'auth/user-not-found' });
  return email;
}

async function reauthenticateWithPassword(user: User, password: string): Promise<void> {
  const email = requireEmail(user);
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(email, password));
}

export async function reauthenticateWithGoogle(user: User): Promise<void> {
  await reauthenticateWithPopup(user, googleProvider);
}

/** A fresh sign-in for a sensitive action (payroll details reveal): password or Google. */
export async function reauthenticateForSensitiveAction(user: User, how: { password: string } | { google: true }): Promise<void> {
  if ('password' in how) await reauthenticateWithPassword(user, how.password);
  else await reauthenticateWithGoogle(user);
}

export function validatePasswordStrength(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters.';
  return null;
}

export async function changeAccountEmail(
  user: User,
  newEmail: string,
  currentPassword: string,
): Promise<void> {
  const trimmed = newEmail.trim();
  if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    throw Object.assign(new Error('Invalid email.'), { code: 'auth/invalid-email' });
  }
  await reauthenticateWithPassword(user, currentPassword);
  await verifyBeforeUpdateEmail(user, trimmed, getAuthActionCodeSettings('/?emailUpdated=1'));
  if (db) {
    await setDoc(
      doc(db, 'profiles', user.uid),
      { email: trimmed, updatedAt: serverTimestamp() },
      { merge: true },
    );
  }
}

export async function changeAccountPassword(
  user: User,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const strengthError = validatePasswordStrength(newPassword);
  if (strengthError) {
    throw Object.assign(new Error(strengthError), { code: 'auth/weak-password' });
  }
  await reauthenticateWithPassword(user, currentPassword);
  await updatePassword(user, newPassword);
}

/** Link email/password sign-in to an SSO-only account (e.g. Google). */
export async function linkPasswordToAccount(user: User, newPassword: string): Promise<void> {
  const email = requireEmail(user);
  const strengthError = validatePasswordStrength(newPassword);
  if (strengthError) {
    throw Object.assign(new Error(strengthError), { code: 'auth/weak-password' });
  }
  const credential = EmailAuthProvider.credential(email, newPassword);
  await linkWithCredential(user, credential);
  await user.reload();
}

export type DeleteAccountInput = {
  user: User;
  /** Required when the account has a password provider. */
  currentPassword?: string;
  /** Set when the user already completed reauthenticateWithPopup (Google-only accounts). */
  reauthenticatedViaGoogle?: boolean;
};

export type DeleteAccountResult = {
  deleted: boolean;
  accountType: 'emt' | 'employer_owner' | 'employer_member' | 'institute' | 'other';
  counts: Record<string, number>;
};

/**
 * Deletes the signed-in account and everything keyed to it. A fresh sign-in first (password or
 * Google), then the deleteMyAccount Cloud Function does all of the work on the server
 * (functions/src/accountDeletion.ts): it re-checks the sign-in is recent and that DELETE was typed,
 * and deletes the Auth account last. The caller signs out afterwards.
 */
export async function deleteAccount({
  user,
  currentPassword,
  reauthenticatedViaGoogle,
}: DeleteAccountInput): Promise<DeleteAccountResult> {
  if (reauthenticatedViaGoogle) {
    // Caller already ran reauthenticateWithPopup; session is fresh.
  } else if (currentPassword) {
    await reauthenticateWithPassword(user, currentPassword);
  } else {
    throw Object.assign(new Error('Reauthentication required.'), { code: 'auth/requires-recent-login' });
  }
  // The server reads the sign-in time from the ID token: make sure it carries the fresh one.
  await user.getIdToken(true);
  if (!app) throw new Error('App not initialized');
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const fn = httpsCallable<{ confirm: 'DELETE' }, DeleteAccountResult>(getFunctions(app), 'deleteMyAccount', { timeout: 540_000 });
  const result = await fn({ confirm: 'DELETE' });
  return result.data;
}

export { authAccountError };
